import React from "react";
import ServicePageLayout, { H2, H3, P, UL } from "./ServicePageLayout";

export const meta = {
  title: "Wireless Infrastructure Alberta | Multi-Building & Rural Networks | Epicom Systems",
  metaDescription:
    "Wireless network infrastructure across Southern Alberta — designing multi-building, rural and commercial wireless networks that hold up in the real world. RF planning, PtP/PtMP distribution, backhaul and integration with the rest of the network.",
  canonicalPath: "/wireless-infrastructure",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://epicom.ca/wireless-infrastructure#service",
    "name": "Wireless Network Infrastructure",
    "serviceType": "Wireless network design, RF planning and multi-site distribution",
    "provider": { "@type": "LocalBusiness", "@id": "https://epicom.ca/#business" },
    "areaServed": { "@type": "AdministrativeArea", "name": "Southern Alberta" },
    "description":
      "Design and installation of wireless network infrastructure across Southern Alberta — RF planning, PtP/PtMP distribution, rural and commercial multi-building networks, backhaul and integration with UniFi networking.",
    "url": "https://epicom.ca/wireless-infrastructure",
  },
};

export default function WirelessInfrastructure() {
  return (
    <ServicePageLayout
      title={meta.title}
      metaDescription={meta.metaDescription}
      canonicalPath={meta.canonicalPath}
      overline="Wireless Infrastructure"
      h1="Wireless Networks Designed as Systems, Not Point Solutions"
      intro="A wireless bridge between two buildings is a product. A wireless network that ties a whole operation together — main site, remote buildings, yard cameras, SCADA endpoints, feed towers, secondary offices — is infrastructure. Epicom designs and installs wireless infrastructure across Southern Alberta as a coordinated system, not a pile of standalone links that happen to be in the same yard."
      jsonLd={meta.jsonLd}
      relatedLinks={[
        { to: "/point-to-point-wireless", label: "Point-to-Point Wireless" },
        { to: "/towers", label: "Communication Towers" },
        { to: "/commercial-networking", label: "Commercial Networking" },
      ]}
    >
      <H2>What we mean by wireless infrastructure</H2>
      <UL>
        <><strong className="text-white/90">Backhaul links</strong> — high-throughput <a href="/point-to-point-wireless" className="text-signal hover:underline">PtP</a> connections carrying the site’s core network traffic between buildings, sites or a remote head-end.</>
        <><strong className="text-white/90">Distribution</strong> — point-to-multipoint (PtMP) hubs feeding multiple remote sites from one base station, common for multi-farm and multi-property operations.</>
        <><strong className="text-white/90">Edge endpoints</strong> — cameras, SCADA units, remote controllers and outbuilding switches that live on the wireless network as legitimate members, not afterthoughts.</>
        <><strong className="text-white/90">Integration with the wired network</strong> — the wireless side has to hand traffic to the switches, routers, VLANs and Wi-Fi in a way that makes sense to the person running the site.</>
        <><strong className="text-white/90">Structural support</strong> — real mounts on real{" "}
          <a href="/towers" className="text-signal hover:underline">towers or structures</a>, cable pathways, grounding.</>
      </UL>

      <H2>Why treating wireless as infrastructure matters</H2>
      <P>
        A wireless link installed as a one-off usually works, at first. The trouble shows up
        when the operation grows — a second building gets added, a camera goes in at the
        far end of the yard, a SCADA vendor needs a network drop at the pivot pump — and
        each new requirement gets bolted on with another separate radio pair. What was
        one working link becomes half a dozen unrelated radios all competing for the same
        chunk of spectrum, aimed by different people at different times, on different
        management systems.
      </P>
      <P>
        Wireless as infrastructure means the whole picture is designed together: the
        spectrum plan, the tower/structure heights, the aim, the network segments each
        link carries, the failover paths (if any), and how new endpoints will get added
        over the next few years. It is the same kind of thinking that goes into a wired
        network — just with radios instead of switches.
      </P>

      <H2>What good wireless infrastructure looks like</H2>
      <H3>Site survey and RF planning</H3>
      <P>
        Every real project starts with a survey: measured line of sight between the
        proposed radio locations, path profile including obstructions and Fresnel-zone
        clearance, an evaluation of what else is on the air in the local spectrum, and a
        sensible band selection for each link based on distance, throughput and interference.
      </P>
      <H3>Spectrum discipline</H3>
      <P>
        On a site with multiple links, the bands used for each link have to be chosen so
        they don’t step on each other. That’s trivial to get right on day one and painful
        to fix retroactively.
      </P>
      <H3>Structural discipline</H3>
      <P>
        Radios that were aimed correctly on a warm summer afternoon should still be aimed
        correctly after a winter of wind and ice cycles. That means proper mounts on
        proper{" "}
        <a href="/towers" className="text-signal hover:underline">structures</a> — not
        clamps on a fence post — and cabling that can’t chafe through a shield in the
        first year.
      </P>
      <H3>Sensible integration with the wired side</H3>
      <P>
        Each wireless hop should hand traffic to the wired network cleanly, on the right
        VLAN, with sensible IP addressing, so the person running the site sees one network
        instead of “the network and the wireless thing.”
      </P>

      <H2>Common patterns we install</H2>
      <UL>
        <><strong className="text-white/90">Multi-building farm and industrial operations</strong> — main shop, secondary buildings, yard camera locations, remote pump/pivot endpoints, all fed from a single main site.</>
        <><strong className="text-white/90">Solar and industrial monitoring</strong> — inverters, controllers, monitoring endpoints backhauled to a central control point.</>
        <><strong className="text-white/90">Remote camera and SCADA distribution</strong> — endpoints that need reliable network access far from any wired infrastructure, tied back over engineered links rather than cell modems.</>
        <><strong className="text-white/90">Rural multi-site business networks</strong> — a business with multiple locations near each other tying them together over its own wireless links rather than paying for a dedicated circuit at each site.</>
        <><strong className="text-white/90">Commercial and municipal distribution</strong> — networks that need to reach outbuildings, secondary sites or camera locations across a property.</>
      </UL>

      <H2>Where wireless doesn’t fit — and we’ll tell you</H2>
      <P>
        Wireless is not a universal solvent. It needs line of sight; it doesn’t punch
        cleanly through mature bush or hills; and it doesn’t replace fibre for
        extreme-throughput campus backbones. When we look at a site and the honest answer
        is “this really wants fibre,” we say so.
      </P>

      <H2>Working with Epicom on a wireless infrastructure project</H2>
      <P>
        We start with the whole picture — every building or endpoint the network has to
        reach, what data has to move between them, what’s planned for the next few years —
        and design the wireless side from that. The finished result is documentation and a
        network the customer can actually operate and grow into, not a set of standalone
        radios in different colours pretending to be one system.
      </P>
    </ServicePageLayout>
  );
}
